import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  api,
  Project,
  User,
  Group,
  ProjectUserPermission,
  ProjectGroupPermission,
  PermissionLevel,
} from '../../api';
import { getErrorMessage } from '../../../../shared/src';
import { PermissionSelector } from '../ui/PermissionSelector';
import { CreateProjectModal } from '../modals';
import { ExpandableResourceCell, ExpandableCellItem } from './ExpandableResourceCell';

type Column = 'users' | 'groups';

interface ExpandedCell {
  projectId: string;
  column: Column;
}

interface ProjectCache {
  userPermissions?: ProjectUserPermission[];
  groupPermissions?: ProjectGroupPermission[];
}

export function PermissionsProjectsSubTab() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const [expandedCell, setExpandedCell] = useState<ExpandedCell | null>(null);
  const [projectCache, setProjectCache] = useState<Record<string, ProjectCache>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Single round-trip: the server pre-joins each project's user/group
      // permissions, replacing the old 1 + N request fan-out.
      const overview = await api.getProjectsPermissionsOverview();
      setProjects(overview.projects);
      setUsers(overview.users);
      setGroups(overview.groups);
      setProjectCache(
        Object.fromEntries(
          overview.projects.map((p) => [
            p.id,
            {
              userPermissions: overview.projectUserPermissions[p.id] ?? [],
              groupPermissions: overview.projectGroupPermissions[p.id] ?? [],
            },
          ])
        )
      );
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCell = (projectId: string, column: Column) => {
    if (expandedCell && expandedCell.projectId === projectId && expandedCell.column === column) {
      setExpandedCell(null);
      return;
    }
    setExpandedCell({ projectId, column });
  };

  const handleCreateSuccess = (project: Project) => {
    setProjects([...projects, project]);
    setShowCreateModal(false);
    setActionMessage({ type: 'success', text: `Project "${project.name}" created successfully` });
  };

  const reloadProjectPerms = async (projectId: string) => {
    const { userPermissions, groupPermissions } = await api.getProjectPermissions(projectId);
    setProjectCache((prev) => ({ ...prev, [projectId]: { userPermissions, groupPermissions } }));
  };

  const handleUserPermissionChange = async (
    projectId: string,
    userId: string,
    permission: PermissionLevel | null
  ) => {
    setUpdatingKey(`user-${userId}`);
    try {
      await api.setUserPermission(userId, projectId, permission);
      await reloadProjectPerms(projectId);
      setActionMessage({ type: 'success', text: 'User permission updated' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update permission: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleGroupPermissionChange = async (
    projectId: string,
    groupId: string,
    permission: PermissionLevel | null
  ) => {
    setUpdatingKey(`group-${groupId}`);
    try {
      await api.setGroupPermission(groupId, projectId, permission);
      await reloadProjectPerms(projectId);
      setActionMessage({ type: 'success', text: 'Group permission updated' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update permission: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleStartRename = (project: Project) => {
    setRenamingProjectId(project.id);
    setRenameValue(project.name);
  };

  const handleCancelRename = () => {
    setRenamingProjectId(null);
    setRenameValue('');
  };

  const handleSaveRename = async (projectId: string) => {
    if (!renameValue.trim()) return;
    setActionInProgress(`rename-${projectId}`);
    try {
      const updated = await api.updateProject(projectId, renameValue.trim());
      setProjects(projects.map((p) => (p.id === projectId ? updated : p)));
      setRenamingProjectId(null);
      setRenameValue('');
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to rename project: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (!window.confirm(`Delete project "${projectName}"? This will remove all associated permissions. This action cannot be undone.`)) return;
    setActionInProgress(`delete-${projectId}`);
    try {
      await api.deleteProject(projectId);
      setProjects(projects.filter((p) => p.id !== projectId));
      if (expandedCell?.projectId === projectId) setExpandedCell(null);
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to delete project: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const userItems = (projectId: string): ExpandableCellItem[] => {
    const perms = projectCache[projectId]?.userPermissions ?? [];
    return perms.map((p) => ({
      key: p.user_id,
      label: `${p.user_first_name} ${p.user_last_name}`.trim() || p.user_email,
      level: p.permission,
    }));
  };

  const groupItems = (projectId: string): ExpandableCellItem[] => {
    const perms = projectCache[projectId]?.groupPermissions ?? [];
    return perms.map((p) => ({ key: p.group_id, label: p.group_name, level: p.permission }));
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          Create Project
        </button>
      </div>

      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Users</th>
            <th>Groups</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => {
            const usersExpanded = expandedCell?.projectId === project.id && expandedCell.column === 'users';
            const groupsExpanded = expandedCell?.projectId === project.id && expandedCell.column === 'groups';
            const anyExpanded = usersExpanded || groupsExpanded;
            const isRenaming = renamingProjectId === project.id;
            const isProcessing = actionInProgress?.includes(project.id);
            return (
              <Fragment key={project.id}>
                <tr>
                  <td>
                    {isRenaming ? (
                      <div className="inline-form">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(project.id);
                            if (e.key === 'Escape') handleCancelRename();
                          }}
                          autoFocus
                        />
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleSaveRename(project.id)}
                          disabled={!renameValue.trim() || actionInProgress === `rename-${project.id}`}
                        >
                          Save
                        </button>
                        <button className="btn-small btn-secondary" onClick={handleCancelRename}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="expandable-row-toggle as-static">{project.name}</span>
                    )}
                  </td>
                  <td>{project.slug}</td>
                  <td>
                    <ExpandableResourceCell
                      items={userItems(project.id)}
                      pluralLabel="users"
                      singularLabel="user"
                      expanded={usersExpanded}
                      onToggle={() => handleToggleCell(project.id, 'users')}
                    />
                  </td>
                  <td>
                    <ExpandableResourceCell
                      items={groupItems(project.id)}
                      pluralLabel="groups"
                      singularLabel="group"
                      expanded={groupsExpanded}
                      onToggle={() => handleToggleCell(project.id, 'groups')}
                    />
                  </td>
                  <td className="actions-cell">
                    {isProcessing ? (
                      <span className="processing">Processing...</span>
                    ) : (
                      <div className="action-buttons">
                        <button className="btn-small btn-secondary" onClick={() => handleStartRename(project)}>
                          Rename
                        </button>
                        <button className="btn-small btn-danger" onClick={() => handleDeleteProject(project.id, project.name)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {anyExpanded && (
                  <tr className="expansion-row">
                    <td colSpan={5}>
                      {usersExpanded ? (
                        <UserPermissionsPanel
                          users={users}
                          permissionFor={(userId) =>
                            projectCache[project.id]?.userPermissions?.find((p) => p.user_id === userId)?.permission ?? null
                          }
                          onChange={(userId, permission) => handleUserPermissionChange(project.id, userId, permission)}
                          updatingKey={updatingKey}
                        />
                      ) : (
                        <GroupPermissionsPanel
                          groups={groups}
                          permissionFor={(groupId) =>
                            projectCache[project.id]?.groupPermissions?.find((p) => p.group_id === groupId)?.permission ?? null
                          }
                          onChange={(groupId, permission) => handleGroupPermissionChange(project.id, groupId, permission)}
                          updatingKey={updatingKey}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {projects.length === 0 && (
            <tr>
              <td colSpan={5} className="empty-state">
                No projects yet. Click "Create Project" to add the first project.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}

function UserPermissionsPanel({
  users,
  permissionFor,
  onChange,
  updatingKey,
}: {
  users: User[];
  permissionFor: (userId: string) => PermissionLevel | null;
  onChange: (userId: string, permission: PermissionLevel | null) => void;
  updatingKey: string | null;
}) {
  const sorted = useMemo(
    () => [...users].sort((a, b) => {
      const aLabel = `${a.first_name} ${a.last_name}`.trim() || a.email;
      const bLabel = `${b.first_name} ${b.last_name}`.trim() || b.email;
      return aLabel.localeCompare(bLabel);
    }),
    [users]
  );
  if (sorted.length === 0) {
    return <div className="empty-state">No users available.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>User Permissions</h5>
      <table className="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Permission</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((user) => (
            <tr key={user.id}>
              <td>
                {user.first_name} {user.last_name}
                <span className="expandable-row-detail">{user.email}</span>
              </td>
              <td>
                <PermissionSelector
                  currentPermission={permissionFor(user.id)}
                  isUpdating={updatingKey === `user-${user.id}`}
                  onPermissionChange={(perm) => onChange(user.id, perm)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupPermissionsPanel({
  groups,
  permissionFor,
  onChange,
  updatingKey,
}: {
  groups: Group[];
  permissionFor: (groupId: string) => PermissionLevel | null;
  onChange: (groupId: string, permission: PermissionLevel | null) => void;
  updatingKey: string | null;
}) {
  const sorted = useMemo(
    () => [...groups].sort((a, b) => a.name.localeCompare(b.name)),
    [groups]
  );
  if (sorted.length === 0) {
    return <div className="empty-state">No groups available.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>Group Permissions</h5>
      <table className="data-table">
        <thead>
          <tr>
            <th>Group</th>
            <th>Permission</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((group) => (
            <tr key={group.id}>
              <td>{group.name}</td>
              <td>
                <PermissionSelector
                  currentPermission={permissionFor(group.id)}
                  isUpdating={updatingKey === `group-${group.id}`}
                  onPermissionChange={(perm) => onChange(group.id, perm)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
